import uuidV4 from 'uuid/v4';

import appSettings from 'wanderu-config';
import Logger from 'wanderu-logger';
import { SnowplowService } from 'wanderu-service-analytics';
import CityService from 'wanderu-service-cities';
import GoogleService from 'wanderu-service-google';
import { formatAddressComponents } from 'wanderu-utils';

import { setAllCitiesInactive } from '../ducks/PopularCities';
import StationService from '../services/stationService';
import {
    removePricings,
} from './Pricings';
import {
    setNoDirectStatus,
    setSearching,
    setRedirectTo,
} from './Status';
import {
    formatGoogleLocation,
} from './utils';

const logger = Logger.getLogger('OriginCities');

const NAMESPACE = 'explorer/originCities';

const ADD_ORIGIN_CITY = `${NAMESPACE}/ADD_ORIGIN_CITY`;
const SET_ORIGIN_CITY_ACTIVE = `${NAMESPACE}/SET_ORIGIN_CITY_ACTIVE`;
const SET_ORIGIN_CITY_INACTIVE = `${NAMESPACE}/SET_ORIGIN_CITY_INACTIVE`;
const SET_ORIGIN_CITY_FETCHING = `${NAMESPACE}/SET_ORIGIN_CITY_FETCHING`;
const REMOVE_ORIGIN_CITY_FETCHING = `${NAMESPACE}/REMOVE_ORIGIN_CITY_FETCHING`;
const REMOVE_ORIGIN_CITY = `${NAMESPACE}/REMOVE_ORIGIN_CITY`;

const INITIAL_STATE = {
    fetching: false,
};

export function addOriginCity(city, setActive) {
    return { type: ADD_ORIGIN_CITY, city, setActive };
}

export function fetchCityFromPlaceId(placeId) {
    return async (dispatch) => {
        dispatch(setOriginCityFetching());
        dispatch(setSearching());
        dispatch(setAllCitiesInactive());
        dispatch(removePricings());

        try {
            const city = await CityService.getCityByPlaceId(placeId);
            const { result: popularRoutes } = await CityService
                .getPopularRoutesByWCityId(city.wcityid);

            const originCity = _formatOriginCity(city, popularRoutes, placeId);
            dispatch(addOriginCity(originCity, true));
            return originCity;
        } catch (ex) {
            const domainUserInfo = appSettings.env.isClient
                ? SnowplowService.getDomainUserInfo()
                : {};

            logger.error('fetchCityFromPlaceId', ex, {
                snowplowIds: {
                    networkUserId: SnowplowService.getNetworkUserId(),
                    ...domainUserInfo,
                },
            });

            dispatch(setOriginCityInactive());
            dispatch(setAllCitiesInactive());
            await dispatch(fetchNearbyCitiesByPlaceId(placeId));

            throw ex;
        } finally {
            dispatch(removeOriginCityFetching());
        }
    };
}

export function fetchCityFromUrl(locale, countryState, city) {
    return async (dispatch) => {
        dispatch(setOriginCityFetching());
        dispatch(setSearching());
        dispatch(setAllCitiesInactive());
        dispatch(removePricings());

        try {
            const [country, state] = countryState.split('-');
            const input = `${city}, ${state} ${country}`;
            const results = await GoogleService.getPlacePredictions(input, {
                locale,
                sessiontoken: uuidV4(),
            });
            const { placeId } = results[0];
            await dispatch(fetchCityFromPlaceId(placeId));
        } catch (ex) {
            const domainUserInfo = appSettings.env.isClient
                ? SnowplowService.getDomainUserInfo()
                : {};

            logger.error('fetchCityFromUrl', ex, {
                snowplowIds: {
                    networkUserId: SnowplowService.getNetworkUserId(),
                    ...domainUserInfo,
                },
            });
            dispatch(setOriginCityInactive());
        } finally {
            dispatch(removeOriginCityFetching());
        }
    };
}

export function fetchNearbyCitiesByLatLng(latLng) {
    return async (dispatch) => {
        try {
            const response = await StationService.getStations(latLng);
            const stations = response.result;

            const allNearbyCities = _formatCitiesFromStations(stations);
            const topThreeNearbyCities = allNearbyCities.slice(0, 3);
            let nearbyCities = await Promise.all(
                topThreeNearbyCities
                    .map(_verifyNearbyCity),
            );
            nearbyCities = nearbyCities.filter(Boolean);

            if (nearbyCities.length === 0) {
                return dispatch(setRedirectTo('/explore/'));
            }

            nearbyCities.forEach((city) => dispatch(addOriginCity(city)));
            dispatch(setNoDirectStatus({ nearbyCities }));
        } catch (ex) {
            const domainUserInfo = appSettings.env.isClient
                ? SnowplowService.getDomainUserInfo()
                : {};

            logger.error('fetchNearbyCitiesByLatLng', ex, {
                snowplowIds: {
                    networkUserId: SnowplowService.getNetworkUserId(),
                    ...domainUserInfo,
                },
            });
            dispatch(setNoDirectStatus());
        }
    };
}

export function fetchNearbyCitiesByPlaceId(placeId) {
    return async (dispatch) => {
        try {
            const response = await GoogleService.getPlaceDetails(placeId);
            const city = formatGoogleLocation(response);
            const { lat, lng } = city;

            return dispatch(fetchNearbyCitiesByLatLng({ lat, lng }));
        } catch (ex) {
            const domainUserInfo = appSettings.env.isClient
                ? SnowplowService.getDomainUserInfo()
                : {};

            logger.error('fetchNearbyCitiesByPlaceId', ex, {
                snowplowIds: {
                    networkUserId: SnowplowService.getNetworkUserId(),
                    ...domainUserInfo,
                },
            });
            throw ex;
        }
    };
}

export function removeOriginCity() {
    return { type: REMOVE_ORIGIN_CITY };
}

function _addOriginCity(state, city, setActive) {
    if (city) {
        const activeWcityid = setActive
            ? city.wcityid
            : state.activeWcityid;
        return {
            ...state,
            fetching: false,
            [city.wcityid]: city,
            activeWcityid,
        };
    }
    return state;
}

function _setOriginCityActive(state, wcityid) {
    if (wcityid) {
        return {
            ...state,
            activeWcityid: wcityid,
        };
    }
    return state;
}

function _setOriginCityInactive(state) {
    return { ...state, activeWcityid: undefined };
}

function _setOriginCityFetching(state) {
    return { ...state, fetching: true };
}

function _removeOriginCityFetching(state) {
    return { ...state, fetching: false };
}

function _removeOriginCity(state) {
    return { ...INITIAL_STATE };
}

export default function reducer(state = INITIAL_STATE, action) {
    switch (action.type) {
        case ADD_ORIGIN_CITY:
            return _addOriginCity(state, action.city, action.setActive);
        case SET_ORIGIN_CITY_ACTIVE:
            return _setOriginCityActive(state, action.wcityid);
        case SET_ORIGIN_CITY_INACTIVE:
            return _setOriginCityInactive(state);
        case SET_ORIGIN_CITY_FETCHING:
            return _setOriginCityFetching(state);
        case REMOVE_ORIGIN_CITY_FETCHING:
            return _removeOriginCityFetching(state);
        case REMOVE_ORIGIN_CITY:
            return _removeOriginCity(state);
        default:
            return state;
    }
}

export function removeOriginCityFetching() {
    return { type: REMOVE_ORIGIN_CITY_FETCHING };
}

export function setOriginCityActive(wcityid) {
    return { type: SET_ORIGIN_CITY_ACTIVE, wcityid };
}

export function setOriginCityFetching() {
    return { type: SET_ORIGIN_CITY_FETCHING };
}

function _formatOriginCity(cityData, popularRoutes, placeId) {
    const {
        city,
        state,
        country,
        wcityid,
    } = cityData;
    const {
        departures,
    } = popularRoutes;

    const cityToCityIds = departures.map((arriveCity) => `${wcityid}_${arriveCity.wid}`);

    const formattedAddress = formatAddressComponents(
        city,
        state,
        country,
    );

    const url = `${country}-${state}/${city}`.toLowerCase();

    return {
        cityToCityIds,
        placeId,
        city,
        formattedAddress,
        wcityid,
        url,
    };
}

export function setOriginCityInactive() {
    return { type: SET_ORIGIN_CITY_INACTIVE };
}

function _formatCitiesFromStations(stations) {
    const stationsByWcityid = stations.reduce((stationMap, station) => {
        const { wcityid } = station;

        const formattedCity = {
            wcityid,
            state: station.address.state,
            city: station.address.city,
            placeId: station.google.place_ids[0],
        };

        return { ...stationMap, [wcityid]: formattedCity };
    }, {});

    return Object.values(stationsByWcityid);
}

async function _verifyNearbyCity(nearbyCity) {
    try {
        const { wcityid } = nearbyCity;
        const { result } = await CityService.getCityByWCityId(wcityid);
        const { details } = result[0].google;
        const {
            formatted_address: formattedAddress,
            place_id: placeId,
            name: city,
            address_components: addressComponents,
        } = details;

        const { result: popularRoutes } = await CityService.getPopularRoutesByWCityId(wcityid);
        const { departures } = popularRoutes;

        const cityToCityIds = departures.map((arriveCity) => `${wcityid}_${arriveCity.wid}`);
        const country = addressComponents[3].short_name;
        const state = addressComponents[2].short_name;
        const url = `${country}-${state}/${city}`.toLowerCase();

        if (!cityToCityIds || cityToCityIds.length === 0) {
            return false;
        }

        return {
            cityToCityIds,
            placeId,
            city,
            url,
            formattedAddress,
            wcityid,
        };
    } catch (ex) {
        const domainUserInfo = appSettings.env.isClient
            ? SnowplowService.getDomainUserInfo()
            : {};

        logger.error('_verifyNearbyCity', ex, {
            snowplowIds: {
                networkUserId: SnowplowService.getNetworkUserId(),
                ...domainUserInfo,
            },
        });
        return false;
    }
}
